import { tv, VariantProps } from 'tailwind-variants';
import { AI } from '@/assets/images';
import { ComponentPropsWithoutRef, ReactNode, forwardRef } from 'react';

const button = tv({
	base: 'relative flex items-center justify-center w-fit font-semibold',
	variants: {
		variant: {
			primary: '',
			secondary: 'bg-transparent',
			text: 'ring-0 bg-transparent text-orange hover:ring-0 hover:bg-transparent hover:text-orange-brighter active:text-orange-opaque active:ring-0'
		},
		size: {
			small: 'h-6 text-xs',
			large: 'h-8 text-sm'
		},
		card: {
			true: 'rounded-[20px]'
		},
		disabled: {
			true: 'cursor-not-allowed  text-dm-strong-mid-grey fill-dm-strong-mid-grey hover:ring-2 focus-within:ring-2 active:ring-2'
		},
		smart: {
			true: 'rounded-[8px] active:!ring-2'
		},
		red: {
			true: ''
		},
		fullWidth: {
			true: 'w-full'
		},
		leftIcon: {
			false: ''
		},
		rightIcon: {
			false: ''
		},
		selected: {
			false: '',
			'not-selectable': ''
		}
	},
	compoundVariants: [
		{
			variant: ['primary', 'secondary'],
			disabled: true,
			class: '!ring-2 ring-dm-strong-mid-grey'
		},
		{
			variant: 'text',
			disabled: true,
			class: '!ring-0 hover:!text-dm-strong-mid-grey focus-visible:!text-dm-strong-mid-grey '
		},
		{
			variant: 'text',
			size: 'large',
			class: 'px-2'
		},
		{
			variant: 'text',
			size: 'small',
			class: 'px-4'
		},
		{
			variant: 'primary',
			disabled: true,
			class: 'bg-dm-strong-mid-grey text-dm-black3.5 ring-dm-grey'
		},
		{
			variant: ['primary', 'secondary'],
			class: 'ring-1 ring-inset hover:ring-1 focus-visible:ring-1'
		},
		{
			variant: ['primary', 'secondary'],
			disabled: false,
			class: 'active:ring-2 active:ring-dm-white'
		},
		{
			variant: ['primary', 'secondary'],
			card: false,
			class: 'rounded-[4px]'
		},
		{
			variant: 'primary',
			disabled: false,
			class: 'ring-dm-gold bg-dm-gold text-dm-black1 hover:bg-dm-gold-275 focus-visible:bg-dm-gold-275 active:bg-dm-gold'
		},
		{
			variant: 'secondary',
			disabled: false,
			selected: 'not-selectable',
			red: false,
			class: 'ring-dm-gold text-dm-gold fill-dm-gold hover:!bg-dm-gold hover:text-dm-black1 hover:fill-dm-black1 focus-visible:bg-dm-gold focus-visible:text-dm-black1 focus-visible:fill-dm-black1 active:fill-dm-black1 active:ring-dm-white active:bg-dm-gold active:text-dm-black1'
		},
		{
			variant: 'secondary',
			disabled: false,
			selected: 'not-selectable',
			red: true,
			class: 'ring-dm-red text-dm-red fill-dm-red hover:bg-dm-red hover:text-dm-black1 hover:fill-dm-black1 focus-visible:bg-dm-red focus-visible:text-dm-black1 focus-visible:fill-dm-black1 active:fill-dm-black1 active:ring-dm-white active:bg-dm-red active:text-dm-black1'
		},
		{
			variant: 'secondary',
			card: true,
			selected: false,
			disabled: false,
			class: 'ring-dm-gold !ring-1 text-dm-gold'
		},
		{
			variant: 'secondary',
			card: true,
			selected: true,
			disabled: false,
			class: 'bg-dm-gold text-dm-black1 fill-dm-black1 ring-dm-gold'
		},
		{
			variant: ['primary', 'secondary'],
			size: 'large',
			card: false,
			class: 'px-4 lg:px-6'
		},
		{
			variant: ['primary', 'secondary'],
			size: 'small',
			card: false,
			class: 'px-4'
		},
		{
			variant: ['primary', 'secondary'],
			size: 'large',
			card: true,
			class: 'px-4'
		},
		{
			variant: ['primary', 'secondary'],
			size: 'small',
			card: true,
			class: 'px-2'
		},
		{
			leftIcon: true,
			card: false,
			size: 'large',
			class: 'px-4'
		},
		{
			leftIcon: true,
			card: false,
			size: 'small',
			class: 'px-3'
		},
		{
			rightIcon: true,
			card: false,
			size: 'large',
			class: 'px-4'
		},
		{
			rightIcon: true,
			card: false,
			size: 'small',
			class: 'px-3'
		},
		{
			leftIcon: true,
			card: true,
			size: 'large',
			class: 'px-3'
		},
		{
			leftIcon: true,
			card: true,
			size: 'small',
			class: 'px-2'
		},
		{
			rightIcon: true,
			card: true,
			size: 'large',
			class: 'px-3'
		},
		{
			rightIcon: true,
			card: true,
			size: 'small',
			class: 'px-2'
		},
		{
			smart: true,
			card: true,
			class: 'rounded-[20px]'
		},
		{
			smart: true,
			card: false,
			class: 'rounded-[8px]'
		},
		{
			smart: true,
			disabled: false,
			class: 'hover:ring-1 hover:text-dm-white focus-visible:text-dm-white active:text-dm-white'
		},
		{
			smart: true,
			disabled: true,
			class: 'bg-dm-strong-mid-grey text-dm-black3.5 ring-dm-grey'
		},
		{
			smart: true,
			disabled: false,
			red: false,
			class: 'bg-dm-blue text-dm-white ring-dm-blue hover:!bg-dm-blue2 focus-visible:bg-dm-blue2 active:!bg-dm-blue'
		},
		{
			smart: true,
			disabled: false,
			red: true,
			class: 'bg-dm-orange2 text-dm-white ring-dm-orange2  hover:bg-dm-orange focus-visible:bg-dm-orange active:bg-dm-orange2'
		}
	]
});

type ButtonProps = ComponentPropsWithoutRef<'button'> &
	Omit<VariantProps<typeof button>, 'leftIcon' | 'rightIcon'> & {
		leftIcon?: ReactNode | null;
		rightIcon?: ReactNode | null;
	};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			variant = 'primary',
			card = false,
			size = 'large',
			leftIcon = false,
			rightIcon = false,
			onClick,
			children,
			className,
			disabled = false,
			smart = false,
			red = false,
			fullWidth = false,
			selected,
			...props
		},
		ref
	) => {
		const isSelectableButton = selected !== undefined;

		return (
			<button
				ref={ref}
				type="button"
				className={button({
					variant,
					size,
					card,
					disabled,
					smart,
					red,
					leftIcon: !!leftIcon,
					rightIcon: !!rightIcon,
					selected: isSelectableButton
						? !!selected
						: 'not-selectable',
					fullWidth,
					className
				})}
				onClick={!disabled ? onClick : undefined}
				disabled={disabled}
				{...props}
			>
				<span
					data-space-size={size}
					className="flex items-center justify-center whitespace-nowrap font-semibold data-[space-size='large']:gap-1.5 data-[space-size='small']:gap-1"
				>
					{leftIcon && (
						<span
							data-icon-size={size}
							className="flex items-center justify-center empty:hidden *:data-[icon-size='large']:!h-[16px] *:data-[icon-size='small']:!h-3 *:data-[icon-size='large']:!w-[16px] *:data-[icon-size='small']:!w-3"
						>
							{leftIcon}
						</span>
					)}
					{smart && leftIcon && (
						<AI
							data-icon-size={size}
							data-icon-color={
								!disabled && !red ? 'contrast' : ''
							}
							className="data-[icon-size='large']:h-5 data-[icon-size='small']:h-5 data-[icon-size='large']:w-5 data-[icon-size='small']:w-4 data-[icon-color='contrast']:text-dm-gold"
						/>
					)}

					{children}

					{rightIcon && (
						<span
							data-icon-size={size}
							className="flex items-center justify-center empty:hidden *:data-[icon-size='large']:!h-[16px] *:data-[icon-size='small']:!h-3 *:data-[icon-size='large']:!w-[16px] *:data-[icon-size='small']:!w-3"
						>
							{rightIcon}
						</span>
					)}
					{smart && rightIcon && (
						<AI
							data-icon-size={size}
							data-icon-color={
								!disabled && !red ? 'contrast' : ''
							}
							className="data-[icon-size='large']:h-5 data-[icon-size='small']:h-5 data-[icon-size='large']:w-5 data-[icon-size='small']:w-4 data-[icon-color='contrast']:text-dm-gold"
						/>
					)}
				</span>
			</button>
		);
	}
);

export default Button;
